<template>
	<div class="bg-white border rounded-lg p-4">
		<div class="flex justify-between items-start mb-4">
			<div>
				<h6 class="text-lg uppercase tracking-wider text-gray-500 mb-1">
					Taxe servicii
				</h6>
				<v-label class="text-base">
					Taxe fixe pe comanda, pe regiune. <span class="font-bold">*Preturi fara TVA</span> — TVA-ul se
					adauga separat, iar in configurator apar ca o singura linie cumulata.
				</v-label>
			</div>

			<v-select ref="selectCountry" v-model="selectedCountry" class="flex-grow-0 w-60" :items="countries"
				item-text="name" item-value="code" outlined hide-details>
				<template v-slot:item="{ item }">
					<span class="w-5 h-5 mr-3">{{ item.flag }}</span>
					<span>
						{{ item.name }}
					</span>
					<span v-if="countryHasChanges(item.code)" class="ml-2 text-orange-500">&bull;</span>
				</template>
				<template v-slot:selection="{ item }">
					<span class="w-5 h-5 mr-3">{{ item.flag }}</span>
					<span>
						{{ item.name }}
					</span>
					<span v-if="countryHasChanges(item.code)" class="ml-2 text-orange-500">&bull;</span>
				</template>
			</v-select>
		</div>

		<div v-if="loading" class="progress">
			<div class="indeterminate"></div>
		</div>

		<div v-else-if="!rows">
			<p class="text-red-500 mb-4">Eroare de incarcare!</p>
			<v-btn @click="loadCountry(selectedCountry, true)" color="teal"
				class="uppercase white--text">Reincarca</v-btn>
		</div>

		<v-form v-else v-model="validForm" ref="formRegionFees" @submit.prevent="save">
			<v-text-field v-model="search" dense outlined hide-details clearable class="w-64 mb-4"
				prepend-inner-icon="mdi-magnify" label="Cauta regiune"></v-text-field>

			<table class="w-full max-w-4xl mb-4">
				<thead>
					<tr class="text-left text-gray-500">
						<th class="pb-2 w-3/12">Regiune</th>
						<th v-for="fee in feeKeys" :key="fee" class="pb-2 w-2/12">{{ labels[fee] }}</th>
						<th class="pb-2 w-3/12">Total</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in visibleRows" :key="row.region">
						<td class="py-1 pr-2 align-middle">{{ row.region }}</td>
						<td v-for="fee in feeKeys" :key="fee" class="py-1 pr-2 align-middle">
							<v-text-field v-model="edits[selectedCountry][row.region][fee]" dense type="number" min="0"
								step="10" hide-spin-buttons outlined suffix="eur" hide-details="auto"
								:rules="[rules.required, rules.notNegative]"></v-text-field>
						</td>
						<td class="py-1 align-middle font-medium">{{ rowTotal(row.region) }} eur</td>
					</tr>
				</tbody>
			</table>

			<p v-if="visibleRows.length == 0" class="mb-4">Nicio regiune care sa corespunda cautarii.</p>

			<div class="flex justify-end">
				<v-btn type="submit" color="teal" class="uppercase white--text" :loading="saving"
					:disabled="saving || !hasChanges">Salveaza {{ selectedCountry }}</v-btn>
			</div>
		</v-form>
	</div>
</template>

<script>
import flags from "@/assets/countries-flags.json"
import pricingSection from './pricingSection';

export default {
	name: "RegionServiceFees",
	mixins: [pricingSection],
	data() {
		return {
			validForm: false,
			selectedCountry: 'RO',
			search: '',
			feeKeys: ['measure', 'montage', 'shipping'],
			labels: {
				measure: 'Masurare',
				montage: 'Montaj',
				shipping: 'Transport'
			},
			// per country, so edits survive switching between them: { RO: { Cluj: { measure, … } } }
			edits: {},
			rules: {
				required: value => (value !== '' && value !== null && value !== undefined) || 'Câmp obligatoriu',
				notNegative: value => (!isNaN(parseFloat(value)) && parseFloat(value) >= 0) || 'Valoare invalida',
			},
		};
	},
	computed: {
		loading() {
			return this.section.loadingRegionFees;
		},
		saving() {
			return this.section.savingRegionFees;
		},
		regionFees() {
			return this.section.regionFees;
		},
		// the countries that carry base prices are the ones the fences are sold in, so regions
		// seeded ahead of an expansion stay out of the way until that country is priced
		countries() {
			return Object.keys(this.section.basePrices || {}).map(code => ({
				code: code,
				name: flags[code] ? flags[code].name : code,
				flag: flags[code] ? flags[code].emoji : ''
			}));
		},
		rows() {
			return this.regionFees[this.selectedCountry] || null;
		},
		visibleRows() {
			const term = (this.search || '').trim().toLowerCase();
			if (!this.rows) return [];
			return term ? this.rows.filter(row => row.region.toLowerCase().includes(term)) : this.rows;
		},
		hasChanges() {
			return this.changesFor(this.selectedCountry).length > 0;
		}
	},
	methods: {
		// live sum of what is in the fields, so it follows the edits and not the stored values
		rowTotal(region) {
			const edits = this.edits[this.selectedCountry];
			if (!edits || !edits[region]) return 0;

			const total = this.feeKeys.reduce((sum, fee) => {
				const value = parseFloat(edits[region][fee]);
				return sum + (isNaN(value) ? 0 : value);
			}, 0);

			return Math.round(total * 100) / 100;
		},
		storedFee(country, region, fee) {
			const row = (this.regionFees[country] || []).find(item => item.region == region);
			const stored = row ? row[fee] : null;
			return stored === null || stored === undefined ? null : Number(stored);
		},
		// builds the editable copy of a country from what the store holds
		syncCountry(country) {
			const edits = {};

			(this.regionFees[country] || []).forEach(row => {
				edits[row.region] = {};
				this.feeKeys.forEach(fee => {
					const stored = this.storedFee(country, row.region, fee);
					edits[row.region][fee] = stored === null ? '' : stored;
				});
			});

			this.$set(this.edits, country, edits);
		},
		async loadCountry(country, force) {
			if (force || !this.regionFees[country]) await this.dispatchSection('fetchRegionServiceFees', country);
			if (this.regionFees[country] && (force || !this.edits[country])) this.syncCountry(country);
		},
		// a region counts as changed if any of its three fees differs, and the API takes the
		// whole row, so all three are sent for it
		changesFor(country) {
			const edits = this.edits[country];
			if (!edits) return [];

			return Object.keys(edits).reduce((changed, region) => {
				const values = {};
				let dirty = false;

				this.feeKeys.forEach(fee => {
					values[fee] = Number(parseFloat(edits[region][fee]).toFixed(2));
					if (values[fee] !== this.storedFee(country, region, fee)) dirty = true;
				});

				if (dirty) changed.push(Object.assign({ region: region }, values));
				return changed;
			}, []);
		},
		countryHasChanges(country) {
			return this.changesFor(country).length > 0;
		},
		async save() {
			const valid = await this.$refs.formRegionFees.validate();
			if (!valid) return;

			const country = this.selectedCountry;
			const saved = await this.dispatchSection('saveRegionServiceFees', {
				country: country,
				fees: this.changesFor(country)
			});

			if (saved) this.syncCountry(country);
		}
	},
	watch: {
		selectedCountry(country) {
			this.search = '';
			this.loadCountry(country);
		}
	},
	created() {
		const codes = this.countries.map(country => country.code);

		// RO is the usual default, but fall back so the card still works if it is not priced
		if (codes.length > 0 && !codes.includes(this.selectedCountry)) {
			this.selectedCountry = codes[0];
		} else {
			this.loadCountry(this.selectedCountry);
		}
	}
};
</script>

<style scoped></style>
